import { Eye, Printer } from 'react-feather';
import PageLayout from '../../../components/PageLayout';
import {
  formatDate,
  getItemById,
  getQueryString,
  isAllTrue,
} from '../../../utils/common.util';
import useSWR from 'swr';
import { useState } from 'react';
import PaymentsReportsModal from './PaymentsReportsModal';
import { DatePicker, DatePickerInput } from 'carbon-components-react';
import PrintOutHead from '../../../components/PrintOutHead';
import { PrintView } from '../../../components/PrintView/print-view.component';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import { useUrlState } from '../../../utils/useUrlState';
import Combobox from '../../../components/combobox/Combobox';
import { useDebounceState } from '../../../utils/useDebounceState';

const initialData = {
  dateFrom: '',
  dateTo: '',
  schoolId: '',
};

export default function SchoolStatementReport() {
  const [isOpen, setIsOpen] = useState('');
  const [query, setQuery] = useUrlState(initialData);

  const [searchSchool, setSearchSchool] = useDebounceState('');
  const { data: schools, isLoading: isLoadingSchools } = useSWR(
    `school/list/v2?${getQueryString({
      query: searchSchool,
      schoolType: 'REGULAR',
    })}`,
  );

  const { data, isLoading, error } = useSWR(
    isAllTrue(query.schoolId, query.dateFrom, query.dateTo)
      ? `school/account-statement-report?${getQueryString(query)}`
      : null,
  );

  const dataResponse = data?.response ?? {};
  const entries = dataResponse?.transactions ?? [];

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Admin', to: '/admin' },
        { label: `School Statement`, active: true },
      ]}
      title={
        <div
          className="d-flex align-items-center flex-wrap"
          style={{ gap: '30px' }}
        >
          <h1 className="h-20">School Statement</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('reports')}
          >
            <Eye size="14" strokeWidth="2px" />
            <span>VIEW OTHER REPORTS</span>
          </button>
        </div>
      }
      isLoading={isLoading}
    >
      {/* FILTERS  */}
      <p className="mt-20 text-12">Select all options to show report.</p>
      <div className="mt-10 card-row-3">
        <Combobox
          options={schools ?? []}
          label="School"
          value={
            query.schoolId
              ? schools?.find((s) => s.id === Number(query.schoolId))
              : null
          }
          onChange={(option) => {
            setQuery({ schoolId: option ? option.id : '' });
            setSearchSchool('');
          }}
          onSearch={setSearchSchool} // The combobox now just updates the search term state
          onClear={() => {
            setQuery({ schoolId: '' });
            setSearchSchool('');
          }}
          loading={isLoadingSchools} // Pass the loading state down
          renderSelected={(option) => `${option.name}`}
          renderOption={(option) => `${option.name}`}
          labelKey="fullName"
          valueKey="id"
          searchPlaceholder="Search schools..."
          placeholder=""
        />
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateFrom: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateFrom: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="dateFrom"
            labelText="Start Date"
            value={query.dateFrom}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateFrom: e.target.value })}
          />
        </DatePicker>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          // minDate={
          //   new Date(query.dateFrom ?? '').toISOString().split('T')[0]
          // }
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateTo: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateTo: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="dateTo"
            labelText="End Date"
            value={query.dateTo}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateTo: e.target.value })}
          />
        </DatePicker>
      </div>

      <div className="mt-20">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {entries?.length > 0 && (
          <>
            <PrintOutHead
              title={`School Statement`}
              headings={[
                {
                  label: 'School',
                  value: getItemById(schools, query.schoolId)?.name ?? 'All',
                },
                {
                  label: 'Date Range',
                  value: query.dateFrom
                    ? `${formatDate(query.dateFrom)} - ${formatDate(
                        query.dateTo,
                      )} `
                    : '-',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                <SchoolStatementTable statement={dataResponse} />
              </div>
            </PrintView>
          </>
        )}
        {entries?.length === 0 && (
          <p className="text-14 mt-30 text-grey">No data available.</p>
        )}

        {entries?.length > 0 && (
          <div
            className="mt-30"
            style={{
              display: 'flex',
              justifySelf: 'end',
              gap: '20px',
              alignItems: 'center',
            }}
          >
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>

      <PaymentsReportsModal
        isOpen={isOpen === 'reports'}
        closeModal={() => setIsOpen('')}
      />
    </PageLayout>
  );
}

const SchoolStatementTable = ({ statement }) => {
  return (
    <div className="mt-20">
      <table className="bx--data-table table-white">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Transaction</th>
            <th>Debit (GH₵)</th>
            <th>Credit (GH₵)</th>
            <th>Balance (GH₵)</th>
          </tr>
        </thead>
        <tbody>
          {/* Opening Balance Row */}
          <tr>
            <td colSpan="5">Opening Balance</td>
            <td>{statement.openingBalance}</td>
          </tr>

          {/* Transaction Rows */}
          {statement.transactions.map((tx) => {
            return (
              <tr key={tx.id}>
                <td>{formatDate(tx.date)}</td>
                <td>{tx.description}</td>
                <td>{tx.transactionType}</td>
                <td>{tx.debit}</td>
                <td>{tx.credit}</td>
                <td>{tx.balance}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="font-bold">
            <td colSpan="3">Total</td>
            <td>{statement.totalDebits}</td>
            <td>{statement.totalCredits}</td>
            <td>{statement.closingBalance}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
